import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import { PURPOSES, DIAL_MODES, STATUS_STYLES, labelFor } from "@/lib/campaignMeta";
import CampaignEditModal from "@/components/CampaignEditModal";
import { CaretLeft, Pause, Play, Archive, Users, CheckCircle, ArrowCounterClockwise, PhoneCall, Sparkle, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";

const TILES = [
    { key: "completed", label: "Calls Completed", tone: "text-ink" },
    { key: "in_progress", label: "In Progress", tone: "text-amber-600" },
    { key: "waiting", label: "Waiting", tone: "text-stone-500" },
    { key: "connected", label: "Connected", tone: "text-emerald-600" },
    { key: "no_answer", label: "No Answer", tone: "text-stone-500" },
    { key: "busy", label: "Busy", tone: "text-orange-500" },
    { key: "voicemail", label: "Voicemail", tone: "text-sky-600" },
    { key: "interested", label: "Interested", tone: "text-emerald-600" },
    { key: "not_interested", label: "Not Interested", tone: "text-red-500" },
    { key: "callback", label: "Callback Requested", tone: "text-violet-600" },
    { key: "converted", label: "Converted", tone: "text-brand" },
    { key: "total", label: "Total Records", tone: "text-ink" },
];

const REC_STATUS_STYLE = {
    pending: "bg-stone-100 text-stone-500",
    in_progress: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
};

export default function CampaignDetail() {
    const { id } = useParams();
    const { orgTimezone } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [records, setRecords] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [editing, setEditing] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: m } = await api.get(`/campaigns/${id}/metrics`);
            setData(m);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
        }
    }, [id]);

    useEffect(() => {
        load();
        api.get(`/campaigns/${id}/records`, { params: { limit: 100 } }).then(({ data }) => setRecords(data)).catch(() => {});
        const t = setInterval(load, 8000); // live refresh (populates during dialing)
        return () => clearInterval(t);
    }, [id, load]);

    const setStatus = async (status, label) => {
        try {
            await api.post(`/campaigns/${id}/status`, { status });
            toast.success(label);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        }
    };

    const refreshRecords = () => api.get(`/campaigns/${id}/records`, { params: { limit: 100 } }).then(({ data }) => setRecords(data)).catch(() => {});
    const simulate = async () => {
        try {
            const { data } = await api.post(`/campaigns/${id}/simulate`);
            toast.success(`Simulated ${data.simulated} calls`);
            load(); refreshRecords();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Nothing to simulate");
        }
    };
    const resetSim = async () => {
        try {
            const { data } = await api.post(`/campaigns/${id}/reset-simulation`);
            toast.success(`Reset ${data.reset} records`);
            load(); refreshRecords();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
        }
    };

    const refreshFromSf = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post(`/campaigns/${id}/refresh-datasource`);
            toast.success(data.added > 0 ? `Added ${data.added} new record${data.added === 1 ? "" : "s"} from Salesforce` : "No new records found — you're up to date");
            load(); refreshRecords();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    const deleteCampaign = async () => {
        if (!window.confirm("Permanently delete this campaign and all its records? This cannot be undone.")) return;
        try {
            await api.delete(`/campaigns/${id}`);
            toast.success("Campaign deleted");
            navigate("/campaigns");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    if (notFound) return <AppShell><div className="mx-auto max-w-4xl px-6 py-16 text-center text-stone2">Campaign not found. <button className="text-brand underline" onClick={() => navigate("/campaigns")}>Back to campaigns</button></div></AppShell>;
    if (!data) return <AppShell><div className="mx-auto max-w-6xl px-6 py-12 text-stone2">Loading…</div></AppShell>;

    const c = data.campaign;
    const m = data.metrics;

    return (
        <AppShell>
            <div className="mx-auto max-w-6xl px-6 py-8 md:px-12 md:py-12" data-testid="campaign-detail-page">
                <button onClick={() => navigate("/campaigns")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink"><CaretLeft size={15} /> All campaigns</button>

                <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{c.name}</h1>
                            <span data-testid="campaign-status" className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLES[c.status]}`}>{c.status}</span>
                        </div>
                        <p className="mt-1.5 text-sm text-stone2">{labelFor(PURPOSES, c.purpose)} · {labelFor(DIAL_MODES, c.dialing_mode)} · {c.total_records} records · created {formatDateTime(c.created_at, orgTimezone)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {c.status === "active" && <button onClick={() => navigate(`/campaigns/${id}/run`)} data-testid="detail-run" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"><PhoneCall size={15} weight="fill" /> Run Campaign</button>}
                        <button onClick={() => setEditing(true)} data-testid="detail-edit" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink"><PencilSimple size={15} /> Edit</button>
                        {(c.data_source?.kind || "").startsWith("sf_") || (c.data_source?.kind || "").startsWith("existing_") ? (
                            <button onClick={refreshFromSf} disabled={refreshing} data-testid="detail-refresh-sf" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink disabled:opacity-50"><ArrowsClockwise size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh from Salesforce"}</button>
                        ) : null}
                        <button onClick={simulate} data-testid="detail-simulate" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink"><Sparkle size={15} /> Simulate run</button>
                        <button onClick={resetSim} data-testid="detail-reset-sim" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone2 hover:border-ink"><ArrowCounterClockwise size={15} /> Reset</button>
                        {c.status === "active" && <button onClick={() => setStatus("paused", "Campaign paused")} data-testid="detail-pause" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink"><Pause size={15} weight="fill" /> Pause</button>}
                        {(c.status === "paused" || c.status === "draft") && <button onClick={() => setStatus("active", "Campaign activated")} data-testid="detail-activate" className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white"><Play size={15} weight="fill" /> {c.status === "draft" ? "Activate" : "Resume"}</button>}
                        {c.status !== "archived" && <button onClick={() => setStatus("archived", "Campaign archived")} data-testid="detail-archive" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink"><Archive size={15} /> Archive</button>}
                        {c.status === "archived" && <button onClick={() => setStatus("draft", "Campaign restored to draft")} data-testid="detail-restore" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold hover:border-ink"><ArrowCounterClockwise size={15} /> Restore to draft</button>}
                        <button onClick={deleteCampaign} data-testid="detail-delete" className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:border-red-500 hover:bg-red-50"><Trash size={15} /> Delete</button>
                    </div>
                </div>

                {/* Progress */}
                <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-ink">Campaign Progress</span>
                        <span className="tabular-nums text-stone2">{m.completed}/{m.total} · {c.progress}% · {c.success_rate}% success</span>
                    </div>
                    <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-stone-200"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${c.progress}%` }} /></div>
                </div>

                {/* Metric tiles */}
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="campaign-metrics">
                    {TILES.map((t) => (
                        <div key={t.key} className="rounded-xl border border-stone-200 bg-white p-4">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-stone2">{t.label}</div>
                            <div className={`mt-1 font-heading text-3xl font-bold tabular-nums ${t.tone}`} data-testid={`metric-${t.key}`}>{m[t.key] ?? 0}</div>
                        </div>
                    ))}
                </div>

                {/* Agent performance */}
                <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2"><Users size={14} weight="fill" /> Agent Performance</div>
                    {data.agents.length === 0 ? (
                        <p className="mt-3 text-sm text-stone2">No calls yet. Agent stats appear here once dialing begins.</p>
                    ) : (
                        <div className="mt-3 space-y-2">
                            {data.agents.map((a, i) => (
                                <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-sm">
                                    <span className="font-semibold text-ink">{a.agent}</span>
                                    <span className="text-stone2">{a.calls} calls · <span className="text-emerald-600">{a.converted} converted</span></span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Records */}
                <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white">
                    <div className="border-b border-stone-200 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Records ({c.total_records})</div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-sm" data-testid="campaign-records">
                            <thead className="border-b border-stone-200 bg-stone-50 text-[11px] font-bold uppercase tracking-wider text-stone2">
                                <tr><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Phone</th><th className="px-4 py-2.5">Company</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Disposition</th><th className="px-4 py-2.5">Attempts</th></tr>
                            </thead>
                            <tbody>
                                {records.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-stone2">No records.</td></tr>}
                                {records.map((r) => (
                                    <tr key={r.id} className="border-b border-stone-100 last:border-0">
                                        <td className="px-4 py-2.5 font-medium text-ink">{r.name || "—"}</td>
                                        <td className="px-4 py-2.5 tabular-nums text-stone2">{r.phone}</td>
                                        <td className="px-4 py-2.5 text-stone2">{r.company || "—"}</td>
                                        <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REC_STATUS_STYLE[r.status] || "bg-stone-100 text-stone-500"}`}>{r.status}</span></td>
                                        <td className="px-4 py-2.5 text-stone2">{r.disposition || "—"}</td>
                                        <td className="px-4 py-2.5 tabular-nums text-stone2">{r.attempts}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {editing && <CampaignEditModal campaign={c} onClose={() => setEditing(false)} onSaved={load} />}
        </AppShell>
    );
}
